import { useEffect, useMemo, useRef, useState } from 'react';
import { TierBox } from '@/components/TierBox';
import { CompanyLogo, useCompanyLogos } from '@/components/CompanyLogo';
import { Building2, Check, ChevronDown, Search, X } from 'lucide-react';
import { useCompanies, type CompanyLinks, type Product } from '@/api/insights';
import { useClientScope, type ClientScope } from '@/contexts/ClientScopeContext';
import { cn } from '@/lib/utils';

/**
 * Header client picker — sets the global client scope (ClientScopeContext). Lists every Autotask
 * company that has data in at least one module (the Insights company index), with recent picks
 * first and the products each client is in. While scoped it turns into a coloured chip with a
 * clear button, so nobody forgets they're looking at one client. Alt+C opens it. Clients with a logo
 * (CompanyLogo, 2026-11-05) show it on the chip and in the list.
 */

const PRODUCT_SHORT: Record<Product, string> = { rmm: 'RMM', cipp: 'M365', csp: 'CSP', backup: 'Backup', network: 'Net', domains: 'DNS', docs: 'Docs', soc: 'SOC' };
/** Pills always appear in this order, each in its own colour, so the same product lines up by eye. */
const PRODUCT_ORDER = Object.keys(PRODUCT_SHORT) as Product[];
const PRODUCT_PILL: Record<Product, string> = {
  rmm: 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200',
  cipp: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200',
  csp: 'bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200',
  backup: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200',
  network: 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-200',
  domains: 'bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-200',
  docs: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200',
  soc: 'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200',
};
const MAX_SHOWN = 60;

export function ClientPicker() {
  const { scope, setScope, recent } = useClientScope();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const companies = useCompanies({ enabled: open || !!scope });
  const logos = useCompanyLogos(open || !!scope).data?.logos ?? {};
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Alt+C anywhere opens the picker.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.key.toLowerCase() === 'c') { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Click outside closes.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!rootRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  useEffect(() => { if (open) { setQ(''); setCursor(0); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);

  const byId = useMemo(() => new Map((companies.data?.companies ?? []).map((c) => [c.id, c])), [companies.data]);
  // Options: "All clients", then recent (when not searching), then every client matching the query.
  const options = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = companies.data?.companies ?? [];
    const matches = s ? all.filter((c) => c.name.toLowerCase().includes(s)) : all;
    const recentRows = s ? [] : recent.map((r) => byId.get(r.companyId) ?? { id: r.companyId, name: r.name, products: [] as Product[] } as CompanyLinks);
    const recentIds = new Set(recentRows.map((r) => r.id));
    return [
      ...(s ? [] : [{ kind: 'all' as const }]),
      ...recentRows.map((c) => ({ kind: 'client' as const, c, recent: true })),
      ...matches.filter((c) => !recentIds.has(c.id)).slice(0, MAX_SHOWN).map((c) => ({ kind: 'client' as const, c, recent: false })),
    ];
  }, [q, companies.data, recent, byId]);

  const choose = (s: ClientScope | null) => { setScope(s); setOpen(false); };
  const pick = (i: number) => {
    const o = options[i];
    if (!o) return;
    choose(o.kind === 'all' ? null : { companyId: o.c.id, name: o.c.name });
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(cursor); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };
  useEffect(() => { setCursor(0); }, [q]);

  return (
    <div ref={rootRef} className="relative">
      {scope ? (
        <div className="flex h-9 items-center overflow-hidden rounded-md border border-brand/40 bg-brand-soft text-sm text-brand-strong">
          <button type="button" onClick={() => setOpen((o) => !o)} title="Change client (Alt+C)"
            className="flex h-full min-w-0 items-center gap-1.5 pl-2.5 pr-2 font-medium hover:bg-brand/10">
            <CompanyLogo url={logos[String(scope.companyId)] ?? null} size={24} className="-ml-1 bg-white ring-1 ring-brand/30"
              fallback={<Building2 className="h-4 w-4 shrink-0" />} />
            <TierBox tier={byId.get(scope.companyId)?.tier} size="xs" />
            <span className="max-w-[320px] truncate" title={scope.name}>{scope.name}</span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
          </button>
          <button type="button" onClick={() => choose(null)} title="Show all clients" aria-label="Clear client"
            className="flex h-full items-center border-l border-brand/30 px-2 hover:bg-brand/10">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen((o) => !o)} title="Pick a client (Alt+C)"
          className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-input bg-background px-2.5 text-sm text-muted-foreground hover:bg-muted/60 hover:text-foreground">
          <Building2 className="h-4 w-4" />
          <span className="hidden sm:inline">All clients</span>
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      )}

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 w-[42rem] max-w-[calc(100vw-11rem)] overflow-hidden max-sm:fixed max-sm:inset-x-3 max-sm:top-14 max-sm:mt-0 max-sm:w-auto max-sm:max-w-none rounded-lg border border-border bg-popover text-popover-foreground shadow-lg" onKeyDown={onKeyDown}>
          <div className="flex items-center gap-2 border-b border-divider px-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a client…" aria-label="Find a client"
              className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
          </div>
          <div className="max-h-[60vh] overflow-y-auto py-1" role="listbox">
            {companies.isLoading && <div className="px-3 py-3 text-xs text-muted-foreground">Loading clients…</div>}
            {options.map((o, i) => {
              const active = i === cursor;
              const firstRecent = o.kind === 'client' && o.recent && (i === 0 || options[i - 1].kind === 'all');
              const firstAll = o.kind === 'client' && !o.recent && (i === 0 || (options[i - 1].kind === 'client' && (options[i - 1] as { recent: boolean }).recent) || options[i - 1].kind === 'all');
              return (
                <div key={o.kind === 'all' ? 'all' : `${o.recent ? 'r' : 'c'}${o.c.id}`}>
                  {firstRecent && <SectionLabel>Recent</SectionLabel>}
                  {firstAll && !q.trim() && <SectionLabel>All clients</SectionLabel>}
                  <button type="button" role="option" aria-selected={active} onMouseEnter={() => setCursor(i)} onClick={() => pick(i)}
                    className={cn('flex w-full flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 text-left text-sm sm:flex-nowrap', active && 'bg-muted')}>
                    {o.kind === 'all' ? (
                      <>
                        <span className="flex-1 text-foreground">All clients</span>
                        {!scope && <Check className="h-3.5 w-3.5 text-muted-foreground" />}
                      </>
                    ) : (
                      <>
                        <span className="flex min-w-0 flex-1 items-center gap-1.5 max-sm:basis-full">
                          <span className="inline-flex w-5 shrink-0 justify-center"><CompanyLogo url={logos[String(o.c.id)] ?? null} size={20} className="bg-white" /></span>
                          <span className="inline-flex w-4 shrink-0 justify-center"><TierBox tier={o.c.tier} size="xs" /></span>
                          <span className="min-w-0 truncate text-foreground" title={o.c.name}>{o.c.name}</span>
                        </span>
                        {/* Fixed-width column: all eight pills fit on one line, names truncate instead. */}
                        <span className="flex shrink-0 flex-nowrap justify-end gap-1 sm:w-[18rem]">
                          {PRODUCT_ORDER.filter((p) => o.c.products.includes(p)).map((p) => (
                            <span key={p} className={cn('rounded px-1 text-[10px] font-medium leading-4', PRODUCT_PILL[p])}>{PRODUCT_SHORT[p]}</span>
                          ))}
                        </span>
                        {scope?.companyId === o.c.id && <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                      </>
                    )}
                  </button>
                </div>
              );
            })}
            {!companies.isLoading && q.trim() && options.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground">No client matches “{q.trim()}”.</div>}
          </div>
          <div className="border-t border-divider px-3 py-1.5 text-[11px] text-muted-foreground">
            Kept until you close the browser · <kbd className="font-mono">Alt+C</kbd>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="px-3 pb-0.5 pt-2 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/75">{children}</div>;
}
